import { useMemo, useState } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';
import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useActivitiesCompletionPlanStore } from 'store/completionPlanPerProgres.store';

import { useMutationExportCompletionPlanToCsv } from 'api/mutations/exportCompletionPlanToCsv.mutation';
import { useQueryActivityCompletionPlan } from 'api/queries/activityCompletionPlan.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { IFilter } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TKeys } from 'components/widgets/FinishDatePredictionCompletionPlan/FinishDatePredictionCompletionPlan.utils';

interface Props {
    componentKey: string;
    widgetId?: string;
    contractId?: number | null | undefined;
    latestVersionId?: number | undefined;
    localFilters: any;
    globalFilterData: any;
    table: UseBaseTable['table'];
    widgetViewMode?: WidgetViewMode;
}

export type UseFinishDatePredictionCompletionPlanProvider = ReturnType<
    typeof useFinishDatePredictionCompletionPlanProvider
>;

export const useFinishDatePredictionCompletionPlanProvider = ({
    componentKey,
    widgetId,
    contractId,
    latestVersionId,
    localFilters,
    globalFilterData,
    table,
    widgetViewMode,
}: Props) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const isInPinboard = Boolean(widgetId);

    //Get initials from store
    const {
        activityTypeFilter: activityTypeFilterStore,
        statusFilter: statusFilterStore,
        submittedCompletionFilter: submittedCompletionFilterStore,
        activeTab: activeTabStore,
        isCriticalPath: isCriticalPathStore,
        isDelayDriver: isDelayDriverStore,
        update: updateStore,
    } = useActivitiesCompletionPlanStore();

    //State based on if it is in report or any other place
    const [activityTypeFilter, setActivityTypeFilter] = useState(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? localFilters?.activityTypeFilter
            : activityTypeFilterStore,
    );
    const [statusFilter, setStatusFilter] = useState(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.statusFilter : statusFilterStore,
    );
    const [submittedCompletionFilter, setSubmittedCompletionFilter] = useState(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? localFilters?.submittedCompletionFilter
            : submittedCompletionFilterStore,
    );
    const [activeTab, setActiveTab] = useState(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ||
            WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode)
            ? localFilters?.activeTab
            : activeTabStore,
    );
    const [isCriticalPath, setIsCriticalPath] = useState(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.isCriticalPath : isCriticalPathStore,
    );
    const [isDelayDriver, setIsDelayDriver] = useState(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.isDelayDriver : isDelayDriverStore,
    );

    //Setters
    const onSetIsDelayDriver = (value) => {
        setIsDelayDriver(value);
        updateStore({ isDelayDriver: value });
    };
    const onSetIsCriticalPath = (value) => {
        setIsCriticalPath(value);
        updateStore({ isCriticalPath: value });
    };
    const onSetSubmittedCompletionFilter = (value) => {
        setSubmittedCompletionFilter(value);
        updateStore({ submittedCompletionFilter: value });
    };
    const onSetStatusFilter = (value) => {
        updateStore({ statusFilter: value });
        setStatusFilter(value);
    };
    const onSetActivityTypeFilter = (value) => {
        updateStore({ activityTypeFilter: value });
        setActivityTypeFilter(value);
    };
    const onSetActiveTab = (value: TKeys) => {
        setActiveTab(value);
        updateStore({ activeTab: value });
        table.setPagination({ ...table.pagination, page: 1 });
    };

    const source = widgetId
        ? { ...localFilters, activeTab }
        : {
              ...globalFilterData,
              activityTypeFilter,
              statusFilter,
              submittedCompletionFilter,
              activeTab,
              isCriticalPath,
              isDelayDriver,
          };

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.ACTIVITY_TYPE_FILTER]: source?.activityTypeFilter?.map((item) => item.value) || [],
            [FilterNames.OPTIONS_STATUS]: source?.statusFilter?.map((item) => item.value) || [],
            [FilterNames.CRITICAL_PATH]: source?.isCriticalPath ? [1] : [],
            [FilterNames.SUBMITTED_COMPLETION_CARD_BOARD]: source?.submittedCompletionFilter,
            [FilterNames.DELAY_DRIVER]: source?.isDelayDriver ? ['true'] : ['false'],
        });
    }, [
        source?.milestoneFragnet,
        source?.activityTypeFilter,
        source?.statusFilter,
        source?.isCriticalPath,
        source?.submittedCompletionFilter,
        source?.isDelayDriver,
    ]);

    const activityCompletionPlanPayload = {
        contractId,
        metaDataId: latestVersionId,
        tabId: source?.activeTab,
        filter: {
            filters: filters,
        },
        sortingRequestList: table.sorting,
        paging: { ...table.pagination, page: table.pagination.page - 1 },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };

    const csvExportMutationPayload = {
        params: {
            contractId,
            metaDataId: latestVersionId,
            tabId: source?.activeTab,
            filter: {
                filters: filters,
            },
            sortingRequestList: table.sorting,
            paging: { ...table.pagination, page: table.pagination.page - 1 },
        },
    };

    const milestoneFilter: IFilter | undefined = filters?.find((filter) => filter.name === FilterNames.FRAGNET_SEARCH);
    const taskVersionHashCode = milestoneFilter?.data?.[0] as string | undefined;

    return {
        ui: {
            state: getState({ source, table }),
            setters: {
                onSetIsDelayDriver,
                onSetIsCriticalPath,
                onSetSubmittedCompletionFilter,
                onSetStatusFilter,
                onSetActivityTypeFilter,
                onSetActiveTab,
            },
            isInPinboard,
            pills: getPills(source, widgetId, latestVersionId),
            vars: {
                taskVersionHashCode,
            },
        },
        api: {
            getActivityCompletionPlan: useQueryActivityCompletionPlan(activityCompletionPlanPayload),
            extractCompletionPlanToCsv: {
                instance: useMutationExportCompletionPlanToCsv(),
                payload: csvExportMutationPayload,
            },
        },
    };
};

const getState = ({ source, table }) => {
    return {
        // globalFilter
        milestoneFragnet: source?.milestoneFragnet,

        //local
        activityTypeFilter: source?.activityTypeFilter,
        statusFilter: source?.statusFilter,
        isCriticalPath: source?.isCriticalPath,
        submittedCompletionFilter: source?.submittedCompletionFilter,
        isDelayDriver: source?.isDelayDriver,
        activeTab: source?.activeTab,

        // table
        sorting: table.sorting,
    };
};

const getPills = (source, widgetId, latestVersionId) => {
    const pillsData = {
        milestoneFragnet: source?.milestoneFragnet,
        activityTypeFilter: source?.activityTypeFilter?.map((item) => item.value) || [],
        statusFilter: source?.statusFilter?.map((item) => item.value) || [],
        isCriticalPath: source?.isCriticalPath ? [1] : [],
        submittedCompletionFilter: source?.submittedCompletionFilter,
        isDelayDriver: source?.isDelayDriver ? ['true'] : ['false'],
    };

    const { pillsList: pills } = useGlobalFiltersHook({
        widgetId,
        latestVersionId,
        source: pillsData,
    });

    return pills;
};
